import useTranslation from 'next-translate/useTranslation'
import omit from 'lodash/omit'
import { useRouter } from 'next/router'
import {
  CSSProperties,
  FC,
  RefObject,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react'
import { Button, FlexboxGrid, Popover, Whisper } from 'rsuite'
import { useClearRefinements } from 'react-instantsearch'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import dynamic from 'next/dynamic'

import useUrls from '../../services/useUrls'
import useForceRemount from '../../services/useForceRemount'
import { AppliedFilters, CatalogType, FilterItem } from '../../utils/types/misc'
import { ProductAttribute, SortOptions } from '../../utils/types/Product'
import {
  MATERIAL_FILTER,
  getCategoryFilters,
  STANDARD_TEST_FILTERS,
  SUPPLIER_FILTER,
  TOP_LEVEL_FILTERS,
} from '../../lib/catalogFilter'
import FilterIcon from '../Icons/FilterIcon'
import useIsUnderSelectedElement from '../hooks/useIsUnderSelectedElement'
import useGlobalAnalyticsParams from '../hooks/useGlobalAnalyticsParams'
import SortPicker from './Pickers/SortPicker'
import { HeaderContext } from '../Layouts/Header/hooks/headerContext'
import { getRefDimension, not } from '../../utils/util'
import useCategoryDataStructure from '../hooks/useCategoryDataStructure'
import useIsScrollingUp from '../hooks/useIsScrollingUp'
import TextFilterPicker from './Pickers/TextFilterPicker'
import PropertyPicker from './Pickers/PropertyPicker'
import { getFeatureValueType } from '../../lib/featureType'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import PiecesPackagePicker from './Pickers/PiecesPackagePicker'
import { getFilterQueryIds } from './Pickers/util'
import { QueryParam, SortOption } from '../../utils/constants'
import useIsOnMobile from '../../services/useIsOnMobile'
import { findFeatureValue, isFilterAttribute } from '../../lib/resources/attribute'
import NumericFilterPicker from './Pickers/NumericFilterPicker'
import ColorMaterialPicker from './Pickers/ColorMaterialPicker'
import TestResultPicker from './Pickers/TestResultPicker'
import SupplierPicker from './Pickers/SupplierPicker'
import FilterTags from './FilterTags'
import useSortStateWithQuery from '../../services/useSortStateWithQuery'
import ColorPicker from './Pickers/ColorPicker'
import SizePicker from './Pickers/SizePicker'

import styles from '../../styles/CatalogFilters.module.less'

const FiltersModal = dynamic(() => import('./FiltersModal'))
interface CatalogHeaderProps {
  catalog: CatalogType
  supplierCode?: string
  listRef: RefObject<HTMLDivElement>
  productCount?: number
  isLoadingProducts?: boolean
}

export type FilterCodes =
  | 'f_standards-base'
  | 'f_label'
  | 'f_use-case'
  | 'f_supid'
  | 'f_color'
  | 'f_material'

const STICKY_FILTER_OFFSET = 300

const CatalogHeader: FC<CatalogHeaderProps> = (props) => {
  const {
    listRef,
    catalog,
    supplierCode,
    productCount,
    isLoadingProducts,
  } = props

  const filtersRef = useRef<HTMLDivElement>(null)
  const filterRefHeight = getRefDimension('height', filtersRef)
  const { t } = useTranslation('products')
  const { pushT } = useUrls()
  const { query, asPath, pathname, route, locale: countryAndLocale } = useRouter()
  const isOnMobile = useIsOnMobile()
  const { locale: lang } = getCountryAndLocaleStrings(countryAndLocale)

  const isScrollingUp = useIsScrollingUp()
  const { parentNodes, categoryWithFilters,
    subCategory, selectedCategory } = useCategoryDataStructure()
  const headerRef = useContext(HeaderContext)
  const headerHeight = getRefDimension('height', headerRef)
  const [globalAnalyticsParams, setGlobalAnalyticsParams] = useGlobalAnalyticsParams()
  const isUnderFilterSection = useIsUnderSelectedElement({
    elementRef: listRef,
    offset: headerHeight - STICKY_FILTER_OFFSET,
  })

  const isNewColorFilter = useFeatureIsOn('product-catalog-color-filter')
  const isNewSizeFilter = useFeatureIsOn('product-catalog-size-filter')

  const shouldCloseFilters = isUnderFilterSection && !isScrollingUp

  // Dynamic styling values to ensure responsiveness
  const [scrollingUpStyle, setScrollingUpStyle] = useState<CSSProperties>({})
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSortSelected, setIsSortSelected] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [renderKey, forceRemount] = useForceRemount()

  const { refine } = useClearRefinements({
    excludedAttributes: [
      `categories.one.${lang}`,
      `categories.two.${lang}`,
      `categories.three.${lang}`,
    ],
  })
  const [sortOption] = useSortStateWithQuery()

  const { actualFilters, moreFilters } = getCategoryFilters(categoryWithFilters)

  // Set product navigation position dynamically
  useEffect(() => {
    if (headerHeight) {
      setScrollingUpStyle({
        transform: `translate3D(0, ${headerHeight}px, 0)`,
      })
    }
  }, [headerHeight])

  // If url contains an advanced filter, open show more
  useEffect(() => {
    const defaultFilters = ['f_standards-base', 'f_label', 'f_use-case', 'f_supid', 'f_color', 'f_material']

    // Url has filter other than default filter
    const advancedFilter = Object.keys(query)
      .find((filterKey) => !defaultFilters.includes(filterKey) && (filterKey.slice(0, 2) === 'f_'))
    if (query && advancedFilter) {
      setShowMoreFilters(true)
    }
  }, [query])

  // to set show more filter option back to its initial state when changing category
  useEffect(() => {
    if (query?.[QueryParam.filters]) {
      setShowMoreFilters(false)
    }
  }, [JSON.stringify(query?.[QueryParam.filters]?.slice(1))])

  const onSortSelect = useCallback((value: SortOptions | null) => {
    if (value) {
      const currentQueries = omit(query, [
        QueryParam.sort,
        QueryParam.filters,
        QueryParam.supplierCode,
      ])
      const { filterInteractionAmount } = globalAnalyticsParams

      // Analytics event
      setGlobalAnalyticsParams({
        ...globalAnalyticsParams,
        selectedFilterValue: value,
        selectedFilterCode: 'sort',
        filterInteractionAmount: +(filterInteractionAmount || 0) + 1,
      })

      const urlWithoutParams = asPath.split('?')?.[0]

      // to remove sort query param
      if (value === SortOption.popular) {
        pushT({
          pathname: urlWithoutParams,
          query: currentQueries,
        }, undefined, { shallow: true })
      } else { // handle adding sort query param
        pushT({
          pathname: urlWithoutParams,
          query: {
            ...currentQueries,
            [QueryParam.sort]: value,
          },
        }, undefined, { shallow: true })
      }
    }
  }, [query, globalAnalyticsParams])

  const handleFiltersButton = () => (showMoreFilters
    ? setShowMoreFilters(false) : setShowMoreFilters(true))

  // Updated function to add or update a filter
  const updateAppliedFilter = useCallback(
    (filterKey: string, filterItem: FilterItem, isNumericFilter?: boolean) => {
      setAppliedFilters((prev) => {
      // If the filter key exists, add item to existing array
        if (prev[filterKey] && !isNumericFilter) {
        // Check if item with same value already exists
          const existingItemIndex = prev[filterKey].findIndex(
            (item) => item.value === filterItem.value,
          )

          if (existingItemIndex >= 0) {
          // Update existing item
            return {
              ...prev,
              [filterKey]: [
                ...prev[filterKey].slice(0, existingItemIndex),
                filterItem,
                ...prev[filterKey].slice(existingItemIndex + 1),
              ],
            }
          }
          // Add new item
          return {
            ...prev,
            [filterKey]: [...prev[filterKey], filterItem],
          }
        }
        // Create new entry for this filter key
        return {
          ...prev,
          [filterKey]: [filterItem],
        }
      })
    }, [],
  )

  // Updated function to remove a filter item
  const removeAppliedFilterItem = useCallback((filterKey: string, value: string | number) => {
    setAppliedFilters((prev) => {
      if (!prev[filterKey]) return prev

      // Filter out the item with matching value
      const updatedFilterItems = prev[filterKey].filter((item) => item.value !== value)
      const updatedFilters = { ...prev, [filterKey]: updatedFilterItems }

      if (updatedFilterItems.length === 0) {
        const { [filterKey]: unusedFilter, ...remainingFilters } = updatedFilters
        return remainingFilters
      }

      return updatedFilters
    })
  }, [])

  // Function to remove all items for a specific filter key
  const removeAppliedFilter = useCallback((filterKey: string) => {
    setAppliedFilters((prev) => Object.keys(prev)
      .filter((key) => key !== filterKey)
      .reduce((newFilters, key) => ({ ...newFilters, [key]: prev[key] }), {} as AppliedFilters))
  }, [])

  // Function to clear all filters
  const clearAllAppliedFilters = useCallback(() => {
    setAppliedFilters({})
  }, [])

  const handleClearAllFilters = useCallback(() => {
    refine()
    const currentQueries = omit(query, getFilterQueryIds(query))

    // Clear applied filters state
    clearAllAppliedFilters()

    pushT({
      pathname: asPath.split('?')?.[0],
      query: currentQueries,
    }, undefined, { shallow: true })
  }, [query, asPath])

  useEffect(() => {
    const filterQueryParams = Object.keys(query).filter((queryParam) => queryParam.startsWith('f_'))
    if (Object.keys(appliedFilters).length > 0) {
      if (filterQueryParams.length === 0 && Object.keys(appliedFilters).length > 0) {
        // Clear filters on navigations that clear all query params
        handleClearAllFilters()
      } else if (!filterQueryParams.some((queryParam) => queryParam in appliedFilters)) {
        // Mega menu click with filter link, clear all old filters and remount component,
        // so that all filters re-read active filters from the url
        forceRemount()
        setAppliedFilters({})
        refine()
      }
    }
  }, [query, clearAllAppliedFilters])

  const filterActions = useMemo(() => ({
    updateAppliedFilter,
    removeAppliedFilter,
    removeAppliedFilterItem,
    clearAllAppliedFilters,
    handleClearAllFilters,
  }), [
    updateAppliedFilter,
    removeAppliedFilter,
    removeAppliedFilterItem,
    clearAllAppliedFilters,
    handleClearAllFilters,
  ])

  const moreFiltersInfo = (
    <Popover className={styles['all-fillters-button-popover']}>
      <b>{t('modals:Looking for more detailed filters?')}</b>
      <p>
        {t('modals:See more exact and detailed filters by visiting')}
        {' '}
        {supplierCode && !selectedCategory ? 'sub categories' : subCategory?.[0]?.catalog?.map((cat) => (
          <span
            key={cat['catalog.code']}
            className={styles['sub-category']}
          >
            {cat['catalog.label']}
          </span>
        ))}
      </p>
    </Popover>
  )

  const isProductCatalogPage = pathname.includes('/products')

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  const getFilterSelectPicker = (filter: ProductAttribute) => {
    const filtersData = filter?.attribute?.filter(not(isFilterAttribute))
    const attributeCode = findFeatureValue(filtersData)?.['attribute.code'] || ''
    const featureValueType = getFeatureValueType(attributeCode)

    if (featureValueType === 'number') {
      return (
        <NumericFilterPicker
          filter={filter}
          shouldCloseFilters={shouldCloseFilters}
          filterActions={filterActions}
        />
      )
    }

    return (
      <TextFilterPicker
        filter={filter}
        shouldCloseFilters={shouldCloseFilters}
        isUnderFilterSection={isUnderFilterSection}
        filterActions={filterActions}
      />
    )
  }

  if (route === '/suppliers') {
    return (
      <div className="margin-bottom-spacer-double">
        <SortPicker
          onSortSelect={onSortSelect}
          catalogType={catalog}
          shouldCloseFilters={shouldCloseFilters}
        />
      </div>
    )
  }

  return (
    <>
      <div
        data-testid="catalog-header"
        className={styles['catalog-header']}
        key={renderKey}
      >
        <FlexboxGrid
          ref={filtersRef}
          align="middle"
          className={`
          ${styles['filters-container']}
          ${isUnderFilterSection ? styles['sticky-header'] : ''}
          ${!isScrollingUp && isUnderFilterSection ? styles['scrolling-down'] : ''}`}
          style={isScrollingUp && isUnderFilterSection ? scrollingUpStyle : {}}
        >
          <FlexboxGrid.Item className={`${styles['filters-section']} max-width-lg`}>
            <FlexboxGrid
              align="middle"
              className={`${styles['catalog-filters']} ${
                isUnderFilterSection
                  ? 'rs-col-sm-offset-5 padding-left-spacer-double'
                  : ''
              }`}
            >
              <SortPicker
                onSortSelect={onSortSelect}
                catalogType={catalog}
                shouldCloseFilters={shouldCloseFilters}
              />
              {TOP_LEVEL_FILTERS?.map(({ id, label, typeSenseFieldName, shouldUseAPISearch }) => (
                <FlexboxGrid.Item key={id}>
                  <PropertyPicker
                    type={id}
                    label={t(label)}
                    typesenseFieldName={typeSenseFieldName === 'use-cases' ? `use-cases_${lang}` : typeSenseFieldName}
                    shouldCloseFilters={shouldCloseFilters}
                    shouldUseAPISearch={shouldUseAPISearch}
                    filterActions={filterActions}
                  />
                </FlexboxGrid.Item>
              ))}
              {isProductCatalogPage && SUPPLIER_FILTER.map(({ id, label }) => (
                <FlexboxGrid.Item key={id}>
                  <SupplierPicker
                    type={id}
                    label={t(label)}
                    appliedFilters={appliedFilters}
                    filterActions={filterActions}
                  />
                </FlexboxGrid.Item>
              ))}
              {actualFilters?.map((filter) => (
                <FlexboxGrid.Item key={filter['attribute.code']}>
                  {getFilterSelectPicker(filter)}
                </FlexboxGrid.Item>
              ))}
              {isNewColorFilter ? (
                <ColorPicker
                  label={t('Color')}
                  shouldCloseFilters={shouldCloseFilters}
                  filterActions={filterActions}
                />
              ) : (
                <ColorMaterialPicker
                  key="color"
                  label={t('Color')}
                  type="color"
                  shouldCloseFilters={shouldCloseFilters}
                />
              )}
              {isNewSizeFilter && (
                <SizePicker
                  label={t('checkout:Size')}
                  shouldCloseFilters={shouldCloseFilters}
                  filterActions={filterActions}
                />
              )}
              <PiecesPackagePicker shouldCloseFilters={shouldCloseFilters} />
              {STANDARD_TEST_FILTERS.map(({ title, category, baseStandardCode, testFilter }) => (
                <TestResultPicker
                  key={category}
                  title={title}
                  shouldCloseFilters={shouldCloseFilters}
                  categoryCode={category}
                  baseStandardCode={baseStandardCode}
                  testFilter={testFilter}
                  filterActions={filterActions}
                />
              ))}
              {parentNodes.length < 2 && MATERIAL_FILTER.map(({ id, label }) => (
                <ColorMaterialPicker
                  key={id}
                  label={t(label)}
                  type={id}
                  shouldCloseFilters={shouldCloseFilters}
                  filterActions={filterActions}
                />
              ))}
              {moreFilters?.map((filter) => (
                <FlexboxGrid.Item
                  className={!showMoreFilters ? styles['hidden-filter'] : ''}
                  key={filter['attribute.code']}
                >
                  {getFilterSelectPicker(filter)}
                </FlexboxGrid.Item>
              ))}
              {(parentNodes.length === 1 || !!moreFilters?.length) && (
                <FlexboxGrid.Item>
                  <Whisper
                    placement="bottomEnd"
                    trigger={parentNodes.length < 2 ? 'hover' : 'none'}
                    speaker={moreFiltersInfo}
                  >
                    <Button
                      appearance="ghost"
                      onClick={parentNodes.length < 2 ? undefined : handleFiltersButton}
                    >
                      <FlexboxGrid align="middle">
                        <FilterIcon />
                        <div className="margin-left-spacer">
                          {showMoreFilters ? t('Close filters') : t('All filters')}
                        </div>
                      </FlexboxGrid>
                    </Button>
                  </Whisper>
                </FlexboxGrid.Item>
              )}
            </FlexboxGrid>
          </FlexboxGrid.Item>
        </FlexboxGrid>
        <FilterTags
          appliedFilters={appliedFilters}
          handleClearAllFilters={handleClearAllFilters}
          removeAppliedFilterItem={removeAppliedFilterItem}
        />
        <div style={{
        // This div was created to eliminate layout shift caused by suddenly disappearing space
        // when filter bar positioning changes from relative to absolute (becomes sticky).
          height: isUnderFilterSection ? filterRefHeight : 0,
        }}
        />
      </div>
      {isOnMobile && (
        <>
          <FlexboxGrid
            className={`${styles['filters-row-gap']} margin-top-spacer margin-bottom-spacer-double`}
            align="middle"
          >
            <FlexboxGrid.Item
              className="flex-grow-1"
              onClick={() => {
                setIsSortSelected(true)
                openModal()
              }}
            >
              <div className={styles['sort-button']}>
                <span className={styles['sort-label']}>
                  {`${t('common:Sort')}:`}
                </span>
                <span className={styles['sort-value']}>
                  {sortOption
                    ? t(sortOption.charAt(0).toUpperCase()
                      + sortOption.slice(1).replace(/-/g, ' ')) : t('Popular')}
                </span>
              </div>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              className="flex-grow-1"
              onClick={() => {
                setIsSortSelected(false)
                openModal()
              }}
            >
              <div className={`${styles['sort-button']} ${Object.keys(appliedFilters).length > 0 ? styles.active : ''}`}>
                <FilterIcon />
                <span className={styles['all-filters']}>{t('All filters')}</span>
                {Object.keys(appliedFilters).length > 0 && (
                <span className="margin-left-spacer">
                  {Object.values(appliedFilters).reduce((count, items) => count + items.length, 0)}
                </span>
                )}
              </div>
            </FlexboxGrid.Item>
          </FlexboxGrid>
          <FilterTags
            appliedFilters={appliedFilters}
            handleClearAllFilters={handleClearAllFilters}
            removeAppliedFilterItem={removeAppliedFilterItem}
          />
          {isModalOpen && (
            <FiltersModal
              appliedFilters={appliedFilters}
              isUnderFilterSection={isUnderFilterSection}
              catalog={catalog}
              supplierCode={supplierCode}
              isSortSelected={isSortSelected}
              productCount={productCount}
              isLoadingProducts={isLoadingProducts}
              onClose={closeModal}
              filterActions={filterActions}
              onSortSelect={onSortSelect}
            />
          )}
        </>
      )}
    </>
  )
}

export default memo(CatalogHeader)
